/**
 * DialogComponent - Gère les modales/boîtes de dialogue
 * Le contenu n'est chargé dans le DOM que lorsqu'on clique pour ouvrir la modal
 */

import { AKComponent } from '@theme/component';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/events';

/**
 * A custom element that manages a dialog.
 * The content is only loaded in the DOM when the dialog is opened.
 *
 * @extends AKComponent
 */
export class DialogComponent extends AKComponent {
    constructor() {
        super();
        this.boundHandleClick = this.#handleClick.bind(this);
        this.boundHandleKeyDown = this.#handleKeyDown.bind(this);
        this.boundHandleResize = this.#handleResize.bind(this);
        this.resizeTimeout = null;
    }

    connectedCallback() {
        super.connectedCallback();

        this.dialog = this.querySelector('dialog');

        if (!this.dialog) {
            console.error('DialogComponent: Missing dialog element');
            return;
        }

        // Setup accessibility after the dialog is found
        this.setupAccessibility();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.boundHandleClick, true);
        document.removeEventListener('keydown', this.boundHandleKeyDown, true);
        window.removeEventListener('resize', this.boundHandleResize);

        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
    }

    /**
     * Setup accessibility attributes for the dialog
     */
    setupAccessibility() {
        if (!this.dialog.hasAttribute('aria-label')) {
            const ariaLabel = this.getAttribute('data-aria-label') || 'Dialog';
            this.dialog.setAttribute('aria-label', ariaLabel);
        }
    }



    /**
     * Shows the dialog immediately without animation.
     */
    showDialog() {
        if (!this.dialog || this.dialog.open) return;

        this.dialog.showModal();

        const dialogOpenEvent = new DialogOpenEvent(this.id, {
            contentUrl: this.dataset.contentUrl,
            contentTemplate: this.dataset.contentTemplate
        });
        this.dispatchEvent(dialogOpenEvent);

        setTimeout(() => {
            document.addEventListener('click', this.boundHandleClick, true);
            document.addEventListener('keydown', this.boundHandleKeyDown, true);
            window.addEventListener('resize', this.boundHandleResize);
            
            // Set initial size immediately
            this.#onResize();
        });
    }

    /**
     * Closes the dialog with animation.
     */
    closeDialog = () => {
        if (!this.dialog || !this.dialog.open) return;

        document.removeEventListener('click', this.boundHandleClick, true);
        document.removeEventListener('keydown', this.boundHandleKeyDown, true);

        // Add closing class for animation
        this.dialog.classList.add('c-dialog-closing');

        // Wait for animation to complete before closing
        const animationSpeed = getComputedStyle(document.documentElement)
            .getPropertyValue('--ak-dialog-animation-speed') || '0.4s';
        const duration = parseFloat(animationSpeed) * 1000;

        setTimeout(() => {
            this.dialog.classList.remove('c-dialog-closing');
            this.dialog.close();
            this.dispatchEvent(new DialogCloseEvent(this.id, { closeReason: 'animated' }));
        }, duration);
    };

    /**
     * Handles window resize with debounce
     * @param {Event} event - The resize event
     */
    #handleResize(event) {
        // Clear existing timeout
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        // Set new timeout with 250ms debounce
        this.resizeTimeout = setTimeout(() => {
            this.#onResize();
        }, 250);
    }

    /**
     * Called after resize debounce delay
     */
    #onResize() {
        if (!this.dialog || !this.dialog.open) return;

        const windowWidth = window.innerWidth;
        const minWidth = parseInt(this.dialog.dataset.minWidth);
        const maxWidth = parseInt(this.dialog.dataset.maxWidth);

        // Calculate dialog width based on window size
        let dialogWidth = Math.max(minWidth, Math.min(maxWidth, windowWidth - 40));

        this.dialog.style.width = `${dialogWidth}px`;
    }

    /**
     * Toggles the dialog.
     */
    toggleDialog = () => {
        if (this.dialog.open) {
            this.closeDialog();
        } else {
            this.showDialog();
        }
    };

    /**
     * Closes the dialog when the user clicks outside of it.
     *
     * @param {MouseEvent} event - The mouse event.
     */
    #handleClick(event) {
        if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
            const rect = this.dialog.getBoundingClientRect();
            const isWithin = event.clientX >= rect.left && event.clientX <= rect.right && 
                           event.clientY >= rect.top && event.clientY <= rect.bottom;
            if (!isWithin) {
                this.closeDialog();
            }
        } else {
            if (!this.dialog.contains(event.target)) {
                this.closeDialog();
            }
        }
    }

    /**
     * Closes the dialog when the user presses the escape key.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    #handleKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeDialog();
        }
    }
}

if (!customElements.get('dialog-component')) {
    customElements.define('dialog-component', DialogComponent);
}

document.addEventListener(
    'toggle',
    (event) => {
        if (event.target instanceof HTMLDialogElement) {
            if (event.target.hasAttribute('scroll-lock')) {
                const { open } = event.target;

                if (open) {
                    document.documentElement.setAttribute('scroll-lock', '');
                } else {
                    document.documentElement.removeAttribute('scroll-lock');
                }
            }
        }
    },
    { capture: true }
); 